/**
 * PATH: packages/email-hub/src/feature/reader-header/adapters/useReaderHeaderCommands.tsx
 * FILE_ID: EMAILHUB-READERHEADER-ADAPTER-COMMANDS-001
 * PURPOSE: Assemble every command-bar/More action from the registry with UNIFORM semantics.
 * ROLE IN ARCHITECTURE: One command source for both adapters — parity by construction.
 * MAIN EXPORTS: useReaderHeaderCommands.
 * NON-RESPONSIBILITIES: Frame layout and overlays live in ReaderHeaderAdapter;
 *   verb transports + timing/undo semantics live in useReaderCoreVerbs/runtime.
 * NOTES FOR FUTURE AI: Overflow order/visibility derives from READER_ACTIONS
 *   (stableOrder + visibleWhen) — never hand-order or usage-sort. Host-varying
 *   behavior enters ONLY through ReaderHeaderHostBindings.
 */
import type { ReactNode } from 'react'
import {
  addressFromEventTarget,
  EMAIL_ADDRESS_ATTR,
  resolveCopiedAddress,
} from '@quotech/ui'
import { useNavigate } from 'react-router-dom'
import {
  Archive,
  BellRing,
  CalendarPlus,
  Clock,
  Copy,
  Eye,
  Inbox,
  Link2,
  MailOpen,
  ShieldAlert,
  Star,
  Trash2,
  ZoomIn,
  ZoomOut,
} from 'lucide-react'
import { triggerComposeFromEmail } from '@quotech/ui/calendar'

import { useToast } from '../../../_internal/components/Toast'
import { useSendEmailToInbox } from '../../hooks/useSendEmailToInbox'
import { buildComposeFromEmailPayload } from '../../lib/composeFromEmail'
import { emailPath } from '../../lib/basePath'
import { deriveJunkTargets } from '../../lib/junkTargets'
import { resolveReaderPosition } from '../../email-reader/navigation'
import { READER_ACTIONS, getReaderAction } from '../actions/registry'
import { buildOverflowRowSubActions } from '../menus/overflowRowItems'
import type { ReaderActionId, ReaderHeaderViewModel } from '../model/types'
import type { ReaderCommandAction, ReaderCommandSubAction } from '../command/CommandBar'
import type { ReaderActionRunner } from '../runtime/useReaderActionRunner'
import { useReaderCoreVerbs } from './useReaderCoreVerbs'
import type { ReaderHeaderHostBindings } from './types'

export interface ReaderHeaderCommands {
  positionLabel?: string
  previous?: ReaderCommandAction
  next?: ReaderCommandAction
  reply: ReaderCommandAction
  replyAll: ReaderCommandAction
  forward: ReaderCommandAction
  done: ReaderCommandAction
  overflowActions: ReaderCommandAction[]
  /** Uniform snooze transport for the adapter-owned SnoozeDialog. */
  onSnoozeSelect: (iso: string) => void
}

interface DialogOpeners {
  openSnooze: () => void
  openVisibility: () => void
}

const icon = (node: ReactNode) => node

/**
 * Build the full command set for one reader surface.
 *
 * Inputs: host bindings, the shared view model, dialog openers owned by the
 * adapter frame, and the adapter's single action runner. Output: core
 * actions + registry-ordered overflow list. Side effects: none at assembly
 * time; core verbs execute through the runner (optimism/undo/classified
 * errors/idempotency/telemetry), local verbs through runner.runLocal.
 * Failure modes: the runner toasts classified copy; nothing throws upward.
 */
export function useReaderHeaderCommands(
  bindings: ReaderHeaderHostBindings,
  model: ReaderHeaderViewModel,
  dialogs: DialogOpeners,
  runner: ReaderActionRunner,
): ReaderHeaderCommands {
  const { email, navigation } = bindings
  const navigate = useNavigate()
  const { toast } = useToast()
  const verbs = useReaderCoreVerbs(bindings, runner)
  const { mutate: sendToInbox, pending: sendingToInbox } = useSendEmailToInbox()

  const position = navigation
    ? resolveReaderPosition(navigation.orderedIds, navigation.currentId)
    : null

  const copyToClipboard = async (text: string, successMessage: string) => {
    try {
      await navigator.clipboard.writeText(text)
      toast(successMessage, 'success')
    } catch {
      toast('Couldn\u2019t copy - your browser blocked clipboard access.', 'error')
    }
  }

  const sendEmailToInbox = async () => {
    if (!bindings.kanban.orgId) {
      toast('Kanban not available - refresh and try again.', 'error')
      return
    }
    try {
      const result = await sendToInbox({
        emailId: String(email.id),
        organisation: bindings.kanban.orgId,
      })
      toast(result.created ? 'Sent to your Inbox' : 'Already in your Inbox', 'success')
    } catch (error) {
      const message =
        (error as Error).message ??
        ((error as { status?: number }).status === 404
          ? 'Set up your Kanban workspace first.'
          : 'Could not send to Inbox.')
      toast(message, 'error')
    }
  }

  const schedule = () => {
    triggerComposeFromEmail(buildComposeFromEmailPayload(email), navigate)
  }

  const copyLink = () =>
    copyToClipboard(
      `${window.location.origin}${emailPath(`/${email.id}`)}`,
      'Link copied - opens this email for anyone on your org.',
    )

  const copyAddress = () => {
    const hovered = typeof document === 'undefined'
      ? null
      : document.querySelector(`[${EMAIL_ADDRESS_ATTR}]:hover`)
    const address = resolveCopiedAddress(
      addressFromEventTarget(hovered),
      email.from_address,
    )
    return copyToClipboard(address, `Copied ${address || 'address'}`)
  }

  /** Navigation verbs: acknowledge + telemetry only, never a spinner. */
  const local = (id: ReaderActionId, fn: () => void) => () =>
    runner.runLocal(getReaderAction(id), fn)

  /** Handler/icon table for registry-driven overflow assembly. */
  const overflowBindings: Partial<
    Record<ReaderActionId, Omit<ReaderCommandAction, 'id' | 'label'> & { label?: string }>
  > = {
    ping: {
      icon: icon(<BellRing className="h-3.5 w-3.5" />),
      onClick: local('ping', () => bindings.onPingOpenChange(true)),
    },
    link_job: bindings.onLinkJob
      ? {
          icon: icon(<Link2 className="h-3.5 w-3.5" />),
          onClick: local('link_job', bindings.onLinkJob),
        }
      : undefined,
    send_to_inbox: {
      icon: icon(<Inbox className="h-3.5 w-3.5" />),
      onClick: () => void sendEmailToInbox(),
      busy: sendingToInbox,
    },
    schedule: {
      icon: icon(<CalendarPlus className="h-3.5 w-3.5" />),
      onClick: local('schedule', schedule),
    },
    who_can_see: {
      icon: icon(<Eye className="h-3.5 w-3.5" />),
      onClick: local('who_can_see', dialogs.openVisibility),
    },
    mark_read: {
      label: email.is_read ? 'Mark unread' : 'Mark read',
      icon: icon(<MailOpen className="h-3.5 w-3.5" />),
      onClick: verbs.toggleRead,
      busy: verbs.spinnerBusy('email:read-state'),
    },
    star: {
      label: email.is_starred ? 'Unstar' : 'Star',
      icon: icon(
        <Star
          className={`h-3.5 w-3.5 ${email.is_starred ? 'fill-amber-400 text-amber-500' : ''}`}
        />,
      ),
      onClick: verbs.toggleStar,
      busy: verbs.spinnerBusy('email:star'),
    },
    snooze: {
      icon: icon(<Clock className="h-3.5 w-3.5" />),
      onClick: local('snooze', dialogs.openSnooze),
      busy: verbs.spinnerBusy('email:snooze'),
    },
    archive: {
      icon: icon(<Archive className="h-3.5 w-3.5" />),
      onClick: verbs.archive,
      busy: verbs.spinnerBusy('email:archive'),
    },
    junk: {
      icon: icon(<ShieldAlert className="h-3.5 w-3.5" />),
      onClick: verbs.junk,
      busy: verbs.spinnerBusy('email:junk'),
      dangerous: true,
    },
    delete: {
      icon: icon(<Trash2 className="h-3.5 w-3.5" />),
      onClick: verbs.del,
      busy: verbs.spinnerBusy('email:delete'),
      dangerous: true,
    },
    zoom_out: bindings.zoom
      ? {
          icon: icon(<ZoomOut className="h-3.5 w-3.5" />),
          onClick: bindings.zoom.onZoomOut,
          disabled: bindings.zoom.value <= bindings.zoom.min,
        }
      : undefined,
    zoom_reset: bindings.zoom
      ? {
          label: `Reset zoom (${Math.round(bindings.zoom.value * 100)}%)`,
          onClick: bindings.zoom.onReset,
        }
      : undefined,
    zoom_in: bindings.zoom
      ? {
          icon: icon(<ZoomIn className="h-3.5 w-3.5" />),
          onClick: bindings.zoom.onZoomIn,
          disabled: bindings.zoom.value >= bindings.zoom.max,
        }
      : undefined,
    copy_sender_address: {
      icon: icon(<Copy className="h-3.5 w-3.5" />),
      onClick: () => void copyAddress(),
    },
    copy_email_link: {
      icon: icon(<Link2 className="h-3.5 w-3.5" />),
      onClick: () => void copyLink(),
    },
  }

  // D269 D1: sender/domain match values for junk's caret rows, mirroring
  // JunkButton/useJunkActions verbatim (same deriveJunkTargets input).
  const { exact: junkExact, domain: junkDomain } = deriveJunkTargets(email.from_address)

  // Registry drives membership AND order; a missing handler means the host
  // genuinely lacks the capability (e.g. hidden job link) and the entry is
  // omitted rather than stubbed.
  const overflowActions: ReaderCommandAction[] = READER_ACTIONS.filter(
    (descriptor) =>
      descriptor.visibility === 'overflow' && descriptor.visibleWhen(model),
  ).flatMap((descriptor) => {
    const binding = overflowBindings[descriptor.id]
    if (!binding) return []
    const { label, ...rest } = binding
    // GENERIC caret: any overflow row whose registry descriptor declares
    // contextActions gets buildOverflowRowSubActions run against it — junk
    // is the only row wired with deps today, but a future overflow action
    // that declares contextActions inherits the caret for free by adding
    // its ids + deps to menus/overflowRowItems.ts, never by touching this
    // flatMap or MoreMenu.tsx.
    const subActions: ReaderCommandSubAction[] =
      descriptor.contextActions.length > 0
        ? buildOverflowRowSubActions(descriptor.id, {
            junk:
              descriptor.id === 'junk'
                ? {
                    exact: junkExact || undefined,
                    domain: junkDomain || undefined,
                    onAutoTrashSender: () => verbs.autoTrash('sender_exact'),
                    onAutoTrashDomain: () => verbs.autoTrash('sender_domain'),
                  }
                : undefined,
          }).map((row) => ({
            id: row.id,
            label: row.label,
            dangerous: row.danger,
            onClick: row.onClick,
          }))
        : []
    return [
      {
        id: descriptor.id,
        label: label ?? descriptor.label,
        ...rest,
        ...(subActions.length > 0 ? { subActions } : {}),
      },
    ]
  })

  // No memo: `bindings` is a fresh object every host render, so the command
  // shape is rebuilt per render by design (all leaves are cheap plain data).
  return {
    positionLabel: position
      ? position.index >= 0
        ? `${position.index + 1} / ${position.total}`
        : `${position.total} loaded`
      : undefined,
    previous: position
      ? {
          id: 'previous',
          label: 'Previous email',
          disabled: !position.previousId,
          onClick: local('previous', () => {
            if (position.previousId) navigation?.onNavigate(position.previousId)
          }),
        }
      : undefined,
    next: position
      ? {
          id: 'next',
          label: 'Next email',
          disabled: !position.nextId,
          onClick: local('next', () => {
            if (position.nextId) navigation?.onNavigate(position.nextId)
          }),
        }
      : undefined,
    reply: {
      id: 'reply',
      label: 'Reply',
      onClick: local('reply', () => bindings.onCompose('reply')),
    },
    replyAll: {
      id: 'reply_all',
      label: 'Reply all',
      onClick: local('reply_all', () => bindings.onCompose('reply_all')),
    },
    forward: {
      id: 'forward',
      label: 'Forward',
      onClick: local('forward', () => bindings.onCompose('forward')),
    },
    done: {
      id: 'done',
      label: 'Done',
      busy: verbs.spinnerBusy('email:done'),
      onClick: verbs.done,
    },
    overflowActions,
    onSnoozeSelect: verbs.snoozeSelect,
  }
}
